import React, { useEffect, useRef, useState } from 'react';

import { useRouter } from 'next/router';

import BackToAppWidget from 'src/components/back-app-widget/BackAppWidget';
import Title, {
  TitleItem
} from 'src/components/consumptionPage/ConsumptionMetaData/Title';
import Tags from 'src/components/Tags';
import { TagLocation, TagProps } from 'src/components/Tags/types';
import { ContentTypes } from 'src/components/thumbnail/types';
import { ConsumptionPageTypes } from 'src/types/consumption';
import { mixpanelObject } from 'src/utils/analytics';
import ConvivaAnalyticHelper, {
  parentalControlConvivaErrorTrigger
} from 'src/utils/analytics/conviva/ConvivaAnalyticHelper';
import { clearCtaEventsProperty } from 'src/utils/analytics/mixpanel/mixpanelHelper';
import {
  deleteSessionStorage,
  getSessionStorage
} from 'src/utils/browserStorageHandler';
import { globalUrlHelper } from 'src/utils/common';
import { ContentItemType } from 'src/utils/commonTypes';
import { DEVICE_TYPES } from 'src/utils/constants/AppConstants';
import {
  BACK_TO_APPWIDGET,
  PARTNER_SOURCE,
  VIBLOCKERFLAG
} from 'src/utils/constants/localStorageConstants';
import {
  B2BConstants,
  VI_BLOCKER_SCREEN
} from 'src/utils/constants/stringConstants';
import { FeatureFlagConstants } from 'src/utils/constants/utilConstants';
import { isFeatureEnabled } from 'src/utils/RemoteConfig';

import { useAppStore } from 'src/hooks/useAppStore';
import {
  MIXPANEL_BUTTON_TYPE,
  MIXPANEL_COLLAPSE_DESCRIPTION,
  MIXPANEL_CTA,
  MIXPANEL_ELEMENT,
  MIXPANEL_EXPAND_DESCRIPTION
} from '../../../utils/analytics/mixpanel/mixpanelConstants';
import { ctaEvent } from '../../../utils/analytics/mixpanel/mixpanelEventsHelper';
import CommerceBanner from './CommerceBanner';
import ContentCrew, { Crew } from './ContentCrew';
import ContentDescription from './ContentDescription';
import ContentSupportedDetails from './ContentSupportedDetails';
import { ValueObject } from './ContentSupportedDetails/SupportedItems';
import SocialButtons from './SocialButtons';
import * as customStyled from './styles';

interface ViewCastAndDirector {
  toggleCrew: () => void;
  showCrew: boolean;
}
export interface ConsumptionMetaDataProps
  extends ConsumptionPageTypes.CommonUIProps {
  contentType: ContentTypes;
  tags: TagProps[];
  titleDetails: TitleItem[];
  subTitle?: string;
  assetInformation: ContentItemType;
  description: string;
  crew: Crew[];
  audioLanguages: ValueObject[];
  subtitles: ValueObject[];
  genre: ValueObject[];
  trailerData: any;
  singlePlayData?: any;
  showWishlist?: () => void;
  handleCommerceLayout?: () => void;
  getSinglePlayDetails?: (spApiObj: any) => void;
}

type ConsumptionMetaDataPropsWithViewCast = ConsumptionMetaDataProps &
  ViewCastAndDirector;

const ConsumptionMetaData: React.FC<ConsumptionMetaDataPropsWithViewCast> = ({
  titleDetails,
  subTitle,
  singlePlayData,
  getSinglePlayDetails,
  tags,
  assetInformation,
  description,
  crew,
  audioLanguages,
  subtitles,
  genre,
  contentType,
  showCrew,
  toggleCrew,
  spaceX,
  trailerData,
  showWishlist,
  handleCommerceLayout
}) => {
  const [showViBackButton, setShowViBackButton] = useState(false);

  const { translations, usersPreferenceData, appConfig, remoteConfig } =
    useAppStore();
  const commerceDataLength = useAppStore().commerceComponentData?.length || 0;

  const { VI_MTV_STRING } = translations.consumption || {};
  const backToAppTxt = VI_MTV_STRING?.Vi_Back_To_Vi_CTA || '';
  const [commerceFeatureFlag, setCommerceFeatureFlag] =
    useState<boolean>(false);
  assetInformation.trailerData = trailerData;

  const router = useRouter();

  let isVIApp = false;
  const deepLink = useRef('');
  const deviceType = appConfig?.deviceType;

  useEffect(() => {
    isVIApp = getSessionStorage(BACK_TO_APPWIDGET);
    if (getSessionStorage(BACK_TO_APPWIDGET)) {
      setShowViBackButton(isVIApp);
      deepLink.current =
        usersPreferenceData?.partner?.config?.blockerScreenConfig?.primary_cta
          ?.deepLink || '';
    }
  }, [appConfig?.isViUser]);

  useEffect(() => {
    return () => {
      const playbackended = window?.contentExit;
      if (playbackended) {
        window.contentExit = '';
        playbackended();
      }
      parentalControlConvivaErrorTrigger(ConvivaAnalyticHelper);
    };
  }, []);

  useEffect(() => {
    const commerce_feature_flag = isFeatureEnabled(
      remoteConfig,
      FeatureFlagConstants.CONFIG_COMMERCE
    );

    setCommerceFeatureFlag(commerce_feature_flag);
  }, [remoteConfig?.length]);

  const trackViweMoreBtnMixpanelEvent = () => {
    mixpanelObject.setProperty(
      MIXPANEL_ELEMENT,
      showCrew ? MIXPANEL_COLLAPSE_DESCRIPTION : MIXPANEL_EXPAND_DESCRIPTION
    );
    mixpanelObject[MIXPANEL_BUTTON_TYPE] = MIXPANEL_CTA;
    ctaEvent();
  };

  const trackBackToVIAppButtonMixpanelEvent = () => {
    clearCtaEventsProperty(mixpanelObject);
    mixpanelObject[MIXPANEL_ELEMENT] = VI_BLOCKER_SCREEN.Vi_Back_To_Vi_CTA;
    mixpanelObject[MIXPANEL_BUTTON_TYPE] = MIXPANEL_CTA;
    ctaEvent();
  };

  const navigationHandler = (link: string, event: Event, btnType: string) => {
    event.preventDefault();
    if (btnType === 'vi') {
      trackBackToVIAppButtonMixpanelEvent();
      if (getSessionStorage(VIBLOCKERFLAG)) {
        deleteSessionStorage(VIBLOCKERFLAG);
      }
      if (getSessionStorage(PARTNER_SOURCE)) {
        deleteSessionStorage(PARTNER_SOURCE);
      }
      window.open(link, '_blank');
    } else {
      router.push(globalUrlHelper(link, true));
    }
  };

  return (
    <>
      {showViBackButton ? (
        <BackToAppWidget
          clickHandler={(e: Event) =>
            navigationHandler(
              deepLink.current ||
                (process.env.NEXT_PUBLIC_VI_APP_DEEPLINK as string),
              e,
              'vi'
            )
          }
          btnText={backToAppTxt}
        />
      ) : null}
      <div
        data-testid="consumption-metadata-wrapper"
        css={customStyled.consumptionMetadataWrapper(spaceX)}>
        <Title
          titleDetails={titleDetails}
          fontSize={24}
          fontWeight={700}
          spaceY={'24'}
        />

        {tags.length ? (
          <Tags
            tagLocation={TagLocation.CONSUMPTION_META_DATA}
            tags={tags}
            spaceY="12"
            spaceX="12"
          />
        ) : null}
        {contentType !== ContentTypes.B2B ? (
          <SocialButtons
            getSinglePlayDetails={getSinglePlayDetails!}
            handleCommerceLayout={handleCommerceLayout}
            singlePlayData={singlePlayData}
            assetInformation={assetInformation}
            spaceX={'12'}
            spaceY={'24'}
          />
        ) : null}
        {commerceFeatureFlag &&
          commerceDataLength > 0 &&
          (deviceType === DEVICE_TYPES.MOBILE ||
            deviceType === DEVICE_TYPES.TABLET) && (
            <CommerceBanner
              showWishlist={showWishlist}
              handleCommerceLayout={handleCommerceLayout}
            />
          )}
        <ContentSupportedDetails
          audioLanguages={audioLanguages}
          subtitles={subtitles}
          genre={genre}
          spaceX={'24'}
          spaceY={'24'}
        />
        {description && (
          <ContentDescription description={description} spaceY={'8'} />
        )}
        {crew.length > 0 && !showCrew && (
          <button
            css={customStyled.viewCrewBtn(showCrew as boolean)}
            onClick={() => {
              trackViweMoreBtnMixpanelEvent();
              toggleCrew();
            }}
            data-testid="view-director-and-cast-btn">
            {B2BConstants.VIEW_DIRECTOR_CAST}
          </button>
        )}
        {crew.length > 0 && showCrew ? (
          <>
            <ContentCrew crew={crew} spaceY={'12'} />
            <button
              css={customStyled.viewCrewBtn(showCrew)}
              onClick={() => {
                trackViweMoreBtnMixpanelEvent();
                toggleCrew();
              }}
              data-testid="view-less-btn">
              {B2BConstants.VIEW_LESS}
            </button>
          </>
        ) : null}
      </div>
    </>
  );
};

export default React.memo(ConsumptionMetaData);
